import React, {
  useEffect,
  useState,
} from "react";

import ProjectApi from "../../api/projectApi";
import TestCaseApi from "../../api/TestCaseApi";
import BugApi from "../../api/BugApi";

function TestingReports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState({
    totalProjects: 0,
    totalTestCases: 0,
    passed: 0,
    failed: 0,
    pending: 0,
    totalBugs: 0,
  });

  const fetchReports = async () => {
    try {
      setLoading(true);

      const projectRes =
        await ProjectApi.assignedTestingProjects();

      console.log(
        "TESTING REPORT PROJECT RESPONSE:",
        projectRes,
      );

      let projects = [];

      if (projectRes?.code === 200) {
        projects =
          projectRes?.data?.projects ||
          projectRes?.data ||
          [];
      }

      if (!Array.isArray(projects)) {
        projects = [];
      }

      const reportData = await Promise.all(
        projects.map(async (project) => {
          let testCases = [];
          let bugs = [];

          try {
            const testCaseRes =
              await TestCaseApi.getProjectTestCases(
                project?._id,
              );

            console.log(
              "TEST CASE RESPONSE:",
              project?._id,
              testCaseRes,
            );

            if (
              testCaseRes?.code === 200 ||
              testCaseRes?.status === 200
            ) {
              testCases =
                testCaseRes?.data?.testCases ||
                testCaseRes?.data ||
                [];
            }

            if (!Array.isArray(testCases)) {
              testCases = [];
            }
          } catch (error) {
            console.error(
              "TEST CASE FETCH ERROR:",
              error,
            );
          }

          try {
            const bugRes =
              await BugApi.getProjectBugs(
                project?._id,
              );

            console.log(
              "BUG RESPONSE:",
              project?._id,
              bugRes,
            );

            if (
              bugRes?.code === 200 ||
              bugRes?.status === 200
            ) {
              bugs =
                bugRes?.data?.bugs ||
                bugRes?.data ||
                [];
            }

            if (!Array.isArray(bugs)) {
              bugs = [];
            }
          } catch (error) {
            console.error(
              "BUG FETCH ERROR:",
              error,
            );
          }

          const passed =
            testCases.filter(
              (item) =>
                item?.status === "passed",
            ).length;

          const failed =
            testCases.filter(
              (item) =>
                item?.status === "failed",
            ).length;

          const pending =
            testCases.filter(
              (item) =>
                item?.status === "pending",
            ).length;

          const blocked =
            testCases.filter(
              (item) =>
                item?.status === "blocked",
            ).length;

          const openBugs =
            bugs.filter(
              (bug) =>
                bug?.status === "open" ||
                bug?.status === "reopened",
            ).length;

          const fixedBugs =
            bugs.filter(
              (bug) =>
                bug?.status === "fixed",
            ).length;

          const closedBugs =
            bugs.filter(
              (bug) =>
                bug?.status === "closed",
            ).length;

          const totalTestCases =
            testCases.length;

          const completionPercentage =
            totalTestCases > 0
              ? Math.round(
                  ((passed + failed + blocked) /
                    totalTestCases) *
                    100,
                )
              : 0;

          return {
            project,
            totalTestCases,
            passed,
            failed,
            pending,
            blocked,
            totalBugs: bugs.length,
            openBugs,
            fixedBugs,
            closedBugs,
            completionPercentage,
          };
        }),
      );

      setReports(reportData);

      const totals = reportData.reduce(
        (acc, item) => {
          acc.totalTestCases +=
            item.totalTestCases;

          acc.passed +=
            item.passed;

          acc.failed +=
            item.failed;

          acc.pending +=
            item.pending;

          acc.totalBugs +=
            item.totalBugs;

          return acc;
        },
        {
          totalProjects:
            reportData.length,

          totalTestCases: 0,
          passed: 0,
          failed: 0,
          pending: 0,
          totalBugs: 0,
        },
      );

      setSummary(totals);
    } catch (error) {
      console.error(
        "TESTING REPORT ERROR:",
        error,
      );

      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const getProjectStatus = (item) => {
    if (item?.totalTestCases === 0) {
      return {
        text: "No Test Cases",
        className:
          "bg-secondary",
      };
    }

    if (item?.failed > 0) {
      return {
        text: "Failed",
        className:
          "bg-danger",
      };
    }

    if (
      item?.pending > 0 ||
      item?.blocked > 0
    ) {
      return {
        text: "In Progress",
        className:
          "bg-warning text-dark",
      };
    }

    if (
      item?.passed ===
      item?.totalTestCases
    ) {
      return {
        text: "Completed",
        className:
          "bg-success",
      };
    }

    return {
      text: "In Progress",
      className:
        "bg-primary",
    };
  };

  if (loading) {
    return (
      <div className="content">
        <div className="text-center py-5">
          <div
            className="spinner-border text-primary mb-3"
            role="status"
          />

          <div>
            Loading Testing Reports...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">

      {/* ================= HEADER ================= */}

      <div className="d-flex justify-content-between align-items-center mb-4">

        <div>
          <h3 className="fw-bold mb-1">
            📊 Testing Reports
          </h3>

          <p className="text-muted mb-0">
            View complete testing performance
            and project reports
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={fetchReports}
        >
          ↻ Refresh
        </button>

      </div>


      {/* ================= SUMMARY CARDS ================= */}

      <div className="row g-4 mb-4">

        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Total Projects
              </div>

              <h3 className="fw-bold mb-0">
                {summary.totalProjects}
              </h3>

            </div>

          </div>

        </div>


        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Test Cases
              </div>

              <h3 className="fw-bold mb-0 text-primary">
                {summary.totalTestCases}
              </h3>

            </div>

          </div>

        </div>


        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Passed
              </div>

              <h3 className="fw-bold mb-0 text-success">
                {summary.passed}
              </h3>

            </div>

          </div>

        </div>


        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Failed
              </div>

              <h3 className="fw-bold mb-0 text-danger">
                {summary.failed}
              </h3>

            </div>

          </div>

        </div>


        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Pending
              </div>

              <h3 className="fw-bold mb-0 text-warning">
                {summary.pending}
              </h3>

            </div>

          </div>

        </div>


        <div className="col-xl-2 col-lg-4 col-md-6">

          <div className="card border-0 shadow-sm h-100">

            <div className="card-body">

              <div className="text-muted small mb-2">
                Total Bugs
              </div>

              <h3 className="fw-bold mb-0 text-danger">
                {summary.totalBugs}
              </h3>

            </div>

          </div>

        </div>

      </div>


      {/* ================= REPORT TABLE ================= */}

      <div className="card border-0 shadow-sm">

        <div className="card-header bg-white border-0 py-3">

          <h5 className="fw-bold mb-0">
            Project Testing Report
          </h5>

        </div>


        <div className="card-body p-0">

          <div className="table-responsive">

            <table className="table table-hover align-middle mb-0">

              <thead className="table-light">

                <tr>

                  <th className="ps-4">
                    Project
                  </th>

                  <th>
                    Total Cases
                  </th>

                  <th>
                    Passed
                  </th>

                  <th>
                    Failed
                  </th>

                  <th>
                    Pending
                  </th>

                  <th>
                    Bugs
                  </th>

                  <th>
                    Progress
                  </th>

                  <th className="pe-4">
                    Status
                  </th>

                </tr>

              </thead>


              <tbody>

                {reports.length > 0 ? (

                  reports.map(
                    (item) => {

                      const projectStatus =
                        getProjectStatus(
                          item,
                        );

                      return (

                        <tr
                          key={
                            item?.project
                              ?._id
                          }
                        >

                          {/* PROJECT */}

                          <td className="ps-4">

                            <div className="fw-bold">

                              {item?.project
                                ?.projectName ||
                                "-"}

                            </div>

                            <small className="text-muted">

                              {
                                item?.project
                                  ?.assignedDeveloper
                                  ?.name ||
                                  "No Developer"
                              }

                            </small>

                          </td>


                          {/* TOTAL */}

                          <td>

                            <strong>

                              {
                                item.totalTestCases
                              }

                            </strong>

                          </td>


                          {/* PASSED */}

                          <td>

                            <span className="badge bg-success px-3 py-2">

                              {
                                item.passed
                              }

                            </span>

                          </td>


                          {/* FAILED */}

                          <td>

                            <span className="badge bg-danger px-3 py-2">

                              {
                                item.failed
                              }

                            </span>

                          </td>


                          {/* PENDING */}

                          <td>

                            <span className="badge bg-warning text-dark px-3 py-2">

                              {
                                item.pending
                              }

                            </span>

                          </td>


                          {/* BUGS */}

                          <td>

                            <div className="small">

                              <span className="text-danger fw-bold">

                                {
                                  item.openBugs
                                }

                              </span>

                              <span className="text-muted">
                                {" "}Open
                              </span>

                            </div>

                            <small className="text-muted">

                              {
                                item.totalBugs
                              } Total

                            </small>

                          </td>


                          {/* PROGRESS */}

                          <td style={{ minWidth: "140px" }}>

                            <div className="d-flex justify-content-between small mb-1">

                              <span>
                                Progress
                              </span>

                              <strong>

                                {
                                  item.completionPercentage
                                }%

                              </strong>

                            </div>


                            <div className="progress">

                              <div
                                className={`progress-bar ${
                                  item.failed > 0
                                    ? "bg-danger"
                                    : item.completionPercentage === 100
                                    ? "bg-success"
                                    : "bg-primary"
                                }`}
                                role="progressbar"
                                style={{
                                  width: `${item.completionPercentage}%`,
                                }}
                                aria-valuenow={
                                  item.completionPercentage
                                }
                                aria-valuemin="0"
                                aria-valuemax="100"
                              />

                            </div>

                          </td>


                          {/* STATUS */}

                          <td className="pe-4">

                            <span
                              className={`badge px-3 py-2 ${projectStatus.className}`}
                            >

                              {
                                projectStatus.text
                              }

                            </span>

                          </td>

                        </tr>

                      );
                    },
                  )

                ) : (

                  <tr>

                    <td
                      colSpan="8"
                      className="text-center py-5 text-muted"
                    >

                      <div
                        style={{
                          fontSize: "45px",
                        }}
                      >
                        📊
                      </div>

                      <div className="fw-bold mt-2">
                        No Testing Reports Available
                      </div>

                      <small>
                        No projects are currently
                        assigned for testing.
                      </small>

                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>

    </div>
  );
}

export default TestingReports;